import React, { useState, useEffect, useMemo, useCallback } from "react";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { Modal, Button } from "react-bootstrap";
import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { createApiClient } from "../utils/apiClient";
import { useUser } from "../utils/userContext";
import { isAdmin as checkIsAdmin, isProjectManager as checkIsProjectManager } from "../utils/access";
import { useTier } from "../utils/tierContext";
import type { StateInfo, ClientInfo, UserInfo, ClientBillingRole, BillingRole, ApiClient } from "../types/UserTypes";
import type { ClientContactsToCreateModel } from "../types/apiSchemaAliases";
import styles from "./Clients.module.css";
import Toast from "./Toast";
import ApproverList from "./ApproverList";
import SearchableSelect from "./SearchableSelect";
import ProjectManagerList from "./ProjectManagerList";
import FeatureGate from "../utils/featureGate";
import {
  EFFECTIVE_DATED_HISTORY_FEATURE,
  EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE,
  getEffectiveHistoryErrorMessage,
  todayYmd,
  withEffectiveStartDateIfAllowed,
} from "./EffectiveDateField";
import SaveEffectiveDateModal from "./SaveEffectiveDateModal";
import HistoricalReportingHistory, {
  HistoryIcon,
  HistoryIconButton,
  formatCurrency,
  formatPercentOrCurrency,
  type HistoryEndpointConfig,
} from "./HistoricalReportingHistory";

type ContactRecord = ClientContactsToCreateModel & { id?: number; contactId?: number };
type UsageCounts = {
  clients: { current: number; max: number | null };
};
type BillingPeriodMode = NonNullable<ClientInfo["billingPeriodMode"]>;

const BILLING_PERIOD_MODE_OPTIONS: Array<{ value: BillingPeriodMode; label: string }> = [
  { value: "unset", label: "Default report ranges" },
  { value: "monthly", label: "Monthly" },
  { value: "semi_monthly", label: "Twice a month" },
];

const formatBillingPeriodMode = (value: unknown) => {
  const normalized = String(value || "unset").trim().toLowerCase();
  return BILLING_PERIOD_MODE_OPTIONS.find((option) => option.value === normalized)?.label || "Default report ranges";
};

const createEmptyClient = () => ({
  clientId: 0,
  businessName: "",
  primaryContactId: null as number | null,
  streetAddress: "",
  city: "",
  state: 48,
  zipCode: "",
  mileageRate: 0,
  adminFee: 0,
  adminFeeType: "percent",
  billingPeriodMode: "unset" as BillingPeriodMode,
});

const toStringValue = (value: unknown, fallback = "") =>
  value === null || value === undefined ? fallback : String(value);

const toNumberValue = (value: unknown, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value: unknown) =>
  value === null || value === undefined ? null : toNumberValue(value);

const normalizeBillingPeriodMode = (value: unknown): BillingPeriodMode => {
  const normalized = toStringValue(value, "unset").trim().toLowerCase();
  return normalized === "monthly" || normalized === "semi_monthly" ? normalized : "unset";
};

const getErrorMessage = (err: unknown, fallback: string) => {
  const error = err as { message?: string; details?: unknown };
  if (error?.details && typeof error.details === "object") {
    const details = error.details as Record<string, unknown>;
    if (typeof details.message === "string" && details.message.trim()) return details.message;
    if (typeof details.detail === "string" && details.detail.trim()) return details.detail;
    if (typeof details.title === "string" && details.title.trim()) return details.title;
  }
  if (typeof error?.details === "string" && error.details.trim()) return error.details.replace(/^"|"$/g, "");
  return error?.message || fallback;
};

const normalizeUsage = (raw: Record<string, unknown>): UsageCounts | null => {
  const clients = raw.clients ?? raw.Clients;
  if (!clients || typeof clients !== "object") return null;

  const clientUsage = clients as Record<string, unknown>;
  return {
    clients: {
      current: toNumberValue(clientUsage.current ?? clientUsage.Current),
      max: toNullableNumber(clientUsage.max ?? clientUsage.Max),
    },
  };
};

const normalizeClient = (raw: Record<string, unknown>): ClientInfo => ({
  ...raw,
  clientId: toNumberValue(raw.clientId ?? raw.ClientId ?? raw.id ?? raw.Id),
  businessName: toStringValue(raw.businessName ?? raw.BusinessName),
  primaryContactId: toNullableNumber(raw.primaryContactId ?? raw.PrimaryContactId),
  primaryContact: (raw.primaryContact ?? raw.PrimaryContact ?? null) as ClientInfo["primaryContact"],
  streetAddress: toStringValue(raw.streetAddress ?? raw.StreetAddress),
  city: toStringValue(raw.city ?? raw.City),
  state: toNumberValue(raw.state ?? raw.State, 48),
  zipCode: toStringValue(raw.zipCode ?? raw.ZipCode),
  mileageRate: toNumberValue(raw.mileageRate ?? raw.MileageRate),
  adminFee: toNumberValue(raw.adminFee ?? raw.AdminFee),
  adminFeeType: toStringValue(raw.adminFeeType ?? raw.AdminFeeType, "percent"),
  billingPeriodMode: normalizeBillingPeriodMode(raw.billingPeriodMode ?? raw.BillingPeriodMode),
  billingRoles: (raw.billingRoles ?? raw.BillingRoles) as ClientBillingRole[] | undefined,
});

function PlusIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 4.167v11.666M4.167 10h11.666"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5.833 6.667v8.75A1.25 1.25 0 0 0 7.083 16.667h5.834a1.25 1.25 0 0 0 1.25-1.25v-8.75M4.167 5h11.666M7.5 5V3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25V5M8.333 9.167v4.166M11.667 9.167v4.166"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M11.05 6.05 13.95 8.95M4.167 15.833l2.34-.26a1 1 0 0 0 .58-.29l7.58-7.58a1.25 1.25 0 0 0 0-1.77l-.94-.94a1.25 1.25 0 0 0-1.77 0l-7.58 7.58a1 1 0 0 0-.29.58z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StarIcon({ filled }: { filled?: boolean }) {
  return (
    <svg viewBox="0 0 20 20" fill={filled ? "currentColor" : "none"} aria-hidden="true">
      <path
        d="M10 2.5l2.39 4.84 5.34.78-3.87 3.77.91 5.33L10 14.9l-4.77 2.51.91-5.33-3.87-3.77 5.34-.78z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SaveIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M4.167 10.833 7.5 14.167 15.833 5.833" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CancelIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5 5l10 10M15 5 5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

// ── Billing Roles Card (self-contained to avoid parent re-renders) ──

interface BillingRolesCardProps {
  clientId: number;
  api: ApiClient;
  showStatus: (type: string, message: string) => void;
  canUseEffectiveHistory: boolean;
}

const ClientBillingRolesCard: React.FC<BillingRolesCardProps> = ({ clientId, api, showStatus, canUseEffectiveHistory }) => {
  const { getFeatureLevel, loading: tierLoading } = useTier();
  const canDeleteRoles = getFeatureLevel("billing_controls") === "advanced";
  const [clientRoles, setClientRoles] = useState<ClientBillingRole[]>([]);
  const [tenantRoles, setTenantRoles] = useState<BillingRole[]>([]);
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [editRoleDayRate, setEditRoleDayRate] = useState<string>("0");
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [addRoleDayRate, setAddRoleDayRate] = useState<string>("");
  const [rolesSaving, setRolesSaving] = useState(false);
  const [roleEffectiveDateModal, setRoleEffectiveDateModal] = useState({
    show: false,
    roleId: 0,
    saving: false,
    error: "",
  });
  const [historyView, setHistoryView] = useState<{
    title: string;
    contextLabel: string;
    configs: HistoryEndpointConfig[];
  } | null>(null);

  // Fetch tenant-level billing roles once
  useEffect(() => {
    (async () => {
      try {
        const data = await api.get<BillingRole[]>("BillingRoles");
        setTenantRoles(Array.isArray(data) ? data.filter(r => r.isActive) : []);
      } catch {
        setTenantRoles([]);
      }
    })();
  }, [api]);

  const loadClientRoles = useCallback(async () => {
    if (!clientId) { setClientRoles([]); return; }
    try {
      const data = await api.get<ClientBillingRole[]>(`ClientRates/${clientId}`);
      setClientRoles(Array.isArray(data) ? data : []);
    } catch {
      setClientRoles([]);
    }
  }, [api, clientId]);

  useEffect(() => {
    loadClientRoles();
  }, [loadClientRoles]);

  // Roles available to add (active tenant roles not yet assigned to this client)
  const unassignedRoles = tenantRoles.filter(
    tr => !clientRoles.some(cr => cr.billingRoleId === tr.id)
  );

  const addClientRole = async () => {
    if (tierLoading) {
      showStatus("error", EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE);
      return;
    }
    const billingRoleId = Number(selectedRoleId);
    if (!billingRoleId) return;
    setRolesSaving(true);
    try {
      await api.post<ClientBillingRole>(`ClientRates/${clientId}`, withEffectiveStartDateIfAllowed({
        billingRoleId,
        dayRate: Number(addRoleDayRate) || 0,
      }, todayYmd(), canUseEffectiveHistory));
      setSelectedRoleId("");
      setAddRoleDayRate("");
      await loadClientRoles();
      showStatus("success", "Billing role added.");
    } catch (err: any) {
      showStatus("error", err?.message || "Failed to add billing role.");
    } finally {
      setRolesSaving(false);
    }
  };

  const updateClientRole = async (roleId: number, effectiveStartDate?: string | null, fromModal = false) => {
    if (tierLoading) {
      showStatus("error", EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE);
      return;
    }
    setRolesSaving(true);
    if (fromModal) setRoleEffectiveDateModal((prev) => ({ ...prev, saving: true, error: "" }));
    try {
      await api.put(`ClientRates/${roleId}`, withEffectiveStartDateIfAllowed({
        dayRate: Number(editRoleDayRate) || 0,
      }, effectiveStartDate, canUseEffectiveHistory));
      setEditingRoleId(null);
      await loadClientRoles();
      showStatus("success", "Rate updated.");
      if (fromModal) setRoleEffectiveDateModal({ show: false, roleId: 0, saving: false, error: "" });
    } catch (err: any) {
      if (fromModal) {
        setRoleEffectiveDateModal((prev) => ({
          ...prev,
          saving: false,
          error: getEffectiveHistoryErrorMessage(err, err?.message || "Failed to update rate."),
        }));
      } else {
        showStatus("error", getEffectiveHistoryErrorMessage(err, err?.message || "Failed to update rate."));
      }
    } finally {
      setRolesSaving(false);
      if (fromModal) {
        setRoleEffectiveDateModal((prev) => (
          prev.show ? { ...prev, saving: false } : prev
        ));
      }
    }
  };

  const confirmClientRoleUpdate = (cr: ClientBillingRole) => {
    if (Number(cr.dayRate || 0) === Number(editRoleDayRate || 0)) {
      updateClientRole(cr.id, null);
      return;
    }
    if (canUseEffectiveHistory) {
      setRoleEffectiveDateModal({ show: true, roleId: cr.id, saving: false, error: "" });
      return;
    }
    updateClientRole(cr.id, null);
  };

  const removeClientRole = async (roleId: number) => {
    setRolesSaving(true);
    try {
      await api.delete(`ClientRates/${roleId}`);
      await loadClientRoles();
      showStatus("success", "Billing role removed.");
    } catch (err: any) {
      showStatus("error", err?.message || "Failed to remove billing role.");
    } finally {
      setRolesSaving(false);
    }
  };

  const openRateHistory = () => {
    if (!clientRoles.length) return;
    setHistoryView({
      title: "Billing Roles History",
      contextLabel: "Billing Roles",
      configs: clientRoles.map((cr) => ({
        sectionLabel: cr.roleName || `Role ${cr.billingRoleId}`,
        readPath: `HistoricalReporting/client-rates/${clientId}/${cr.billingRoleId}`,
        correctionPath: "HistoricalReporting/client-rates",
        rowScopeFields: ["roleName", "dayRate"],
        valueFields: [
          { key: "roleName", label: "Role" },
          { key: "dayRate", label: "Day Rate", format: formatCurrency },
        ],
      })),
    });
  };

  return (
    <div className={styles['clients-card']} data-testid="billing-roles-card">
      <div className={styles['clients-card-header']}>
        <h2 className={styles['clients-card-header-title']}>Billing Roles</h2>
        {canUseEffectiveHistory && clientId > 0 && clientRoles.length > 0 && (
          <HistoryIconButton
            onClick={openRateHistory}
            label="View effective history for billing roles"
            testId="client-billing-roles-history"
          />
        )}
      </div>
      {clientId > 0 ? (
        <>
          {/* Add role composer — shown when unassigned roles exist */}
          {unassignedRoles.length > 0 && (
            <div className={styles['clients-billing-composer']}>
              <div className={styles['clients-field']}>
                <label className={styles['clients-label']}>Role</label>
                <select
                  className={styles['clients-control']}
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  disabled={rolesSaving}
                  aria-label="Select billing role"
                >
                  <option value="">Select a role…</option>
                  {unassignedRoles.map(r => (
                    <option key={r.id} value={r.id}>{r.roleName}</option>
                  ))}
                </select>
              </div>
              <div className={styles['clients-field']}>
                <label className={styles['clients-label']}>Day Rate</label>
                <input
                  className={styles['clients-control']}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={addRoleDayRate}
                  onChange={(e) => setAddRoleDayRate(e.target.value)}
                  disabled={rolesSaving || !selectedRoleId}
                />
              </div>
              <div className={styles['clients-billing-actions']}>
                <button
                  type="button"
                  className={`${styles['clients-btn']} ${styles['clients-billing-add-btn']}`}
                  onClick={addClientRole}
                  disabled={tierLoading || rolesSaving || !selectedRoleId}
                >
                  <PlusIcon />
                  Add Role
                </button>
              </div>
            </div>
          )}

          <div className={styles['clients-card-subheader']}>
            <span>Assigned Rates</span>
          </div>

          {clientRoles.length === 0 ? (
            <div className={styles['clients-empty']}>
              <div className={styles['clients-empty__icon']}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M7 7h10M7 12h6M7 17h8" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" />
                  <rect x="3" y="3" width="18" height="18" rx="3" stroke="#d1d5db" strokeWidth="1.5" />
                </svg>
              </div>
              <p className={styles['clients-empty__title']}>No billing roles</p>
              <p className={styles['clients-empty__subtitle']}>No billing roles have been assigned yet.</p>
            </div>
          ) : (
            <>
              <div className={styles['clients-billing-list-header']}>
                <div>Role</div>
                <div>Day Rate</div>
                <div></div>
              </div>

              {clientRoles.map((cr) => (
                <div className={styles['clients-billing-row']} key={cr.id} data-testid={`client-billing-role-${cr.id}`}>
                  {editingRoleId === cr.id ? (
                    <>
                      <div className={styles['clients-billing-row__cell']}>
                        {cr.roleName}
                      </div>
                      <div className={styles['clients-billing-row__cell']}>
                        <input
                          className={styles['clients-control']}
                          type="text"
                          value={editRoleDayRate}
                          onChange={(e) => setEditRoleDayRate(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className={styles['clients-billing-row__actions']}>
                        <button
                          type="button"
                          className={styles['clients-icon-btn']}
                          onClick={() => confirmClientRoleUpdate(cr)}
                          disabled={tierLoading || rolesSaving}
                          aria-label="Save rate"
                          title="Save"
                        >
                          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path d="M4.167 10l4.166 4.167L15.833 5.833" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className={styles['clients-icon-btn']}
                          onClick={() => setEditingRoleId(null)}
                          disabled={rolesSaving}
                          aria-label="Cancel edit"
                          title="Cancel"
                        >
                          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path d="M5.833 5.833l8.334 8.334M14.167 5.833l-8.334 8.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles['clients-billing-row__cell']}>
                        {cr.roleName}
                      </div>
                      <div className={styles['clients-billing-row__cell']}>
                        ${Number(cr.dayRate).toFixed(2)}
                      </div>
                      <div className={styles['clients-billing-row__actions']}>
                        <button
                          type="button"
                          className={styles['clients-icon-btn']}
                          onClick={() => { setEditingRoleId(cr.id); setEditRoleDayRate(String(cr.dayRate)); }}
                          aria-label={`Edit ${cr.roleName} rate`}
                          title="Edit rate"
                        >
                          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <path d="M13.586 3.586a2 2 0 1 1 2.828 2.828L7.5 15.328l-3.75 1.172 1.172-3.75 8.664-8.664Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                        {canDeleteRoles && (
                        <button
                          type="button"
                          className={`${styles['clients-icon-btn']} ${styles['clients-icon-btn--danger']}`}
                          onClick={() => removeClientRole(cr.id)}
                          disabled={rolesSaving}
                          aria-label={`Remove ${cr.roleName}`}
                          title="Remove role"
                        >
                          <TrashIcon />
                        </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </>
          )}
        </>
      ) : (
        <div className={styles['clients-contact-empty']}>
          Save the client before assigning billing roles.
        </div>
      )}
      <SaveEffectiveDateModal
        show={roleEffectiveDateModal.show}
        title="Confirm Rate Change"
        changedFieldLabels={["Day Rate"]}
        saving={roleEffectiveDateModal.saving}
        errorMessage={roleEffectiveDateModal.error}
        onCancel={() => setRoleEffectiveDateModal({ show: false, roleId: 0, saving: false, error: "" })}
        onConfirm={(effectiveStartDate) => updateClientRole(roleEffectiveDateModal.roleId, effectiveStartDate, true)}
      />
      <HistoricalReportingHistory
        show={!!historyView}
        api={api}
        enabled={canUseEffectiveHistory}
        title={historyView?.title || "Effective History"}
        contextLabel={historyView?.contextLabel || ""}
        config={null}
        configs={historyView?.configs || []}
        onClose={() => setHistoryView(null)}
        onChanged={loadClientRoles}
        onCorrected={(message) => showStatus("success", message)}
      />
    </div>
  );
};

const Clients = () => {
  const authRequest = { ...loginRequest };
  const { token, getToken, error } = useAuthToken();
  const { user, activeTenantId } = useUser();
  const { getLimit, hasFeature, loading: tierLoading } = useTier();
  const canUseEffectiveHistory = hasFeature(EFFECTIVE_DATED_HISTORY_FEATURE);

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user?.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const isPM = checkIsProjectManager(Number(user?.roleId));
  const canEdit = checkIsAdmin(Number(user?.roleId));

  const [clients, setClients]= useState<ClientInfo[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<ClientInfo | null>(null);
  const [states, setStates] = useState<StateInfo[]>([]);
  const [clientContacts, setClientContacts] = useState<ContactRecord[]>([]);
  const [newContact, setNewContact] = useState({
    firstName: "",
    lastName: "",
    emailAddress: "",
    phoneNumber: "",
  });

  const [editingContactId, setEditingContactId] = useState<number | null>(null);
  const [editingContact, setEditingContact] = useState<{ firstName: string; lastName: string; emailAddress: string; phoneNumber: string }>({ firstName: "", lastName: "", emailAddress: "", phoneNumber: "" });

  const [status, setStatus] = useState<string | null>(null); // "success" | "error" | null
  const [statusMessage, setStatusMessage] = useState("");
  const [effectiveDateModal, setEffectiveDateModal] = useState({
    show: false,
    changedFields: [] as string[],
    saving: false,
    error: "",
  });
  const [contactEffectiveDateModal, setContactEffectiveDateModal] = useState({
    show: false,
    contactId: 0,
    changedFields: [] as string[],
    saving: false,
    error: "",
  });
  const [historyView, setHistoryView] = useState<{
    title: string;
    contextLabel: string;
    configs: HistoryEndpointConfig[];
  } | null>(null);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [usage, setUsage] = useState<UsageCounts | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const tierMaxClients = getLimit("max_clients");
  const maxClients = usage?.clients.max ?? null;
  const cappedTier = canEdit && tierMaxClients !== null;
  const cappedTierUsageUnavailable = cappedTier && (usageLoading || !usage);
  const clientLimitReached =
    canEdit && usage !== null && maxClients !== null && usage.clients.current >= maxClients;
  const clientCreationBlocked = cappedTierUsageUnavailable || clientLimitReached;
  const clientLimitMessage =
    maxClients !== null
      ? `${maxClients} client limit reached on FreeCrew. Upgrade to add more clients.`
      : "";

  const showStatus = useCallback((nextStatus: string, message: string) => {
    setStatus(nextStatus);
    setStatusMessage(message);
  }, []);

  const openClientProfileHistory = () => {
    const clientId = Number(selectedClient?.clientId || 0);
    if (!clientId) return;
    setHistoryView({
      title: "Client Details History",
      contextLabel: `${selectedClient?.businessName || `Client ${clientId}`} - Client Details`,
      configs: [{
        sectionLabel: "Client Details",
        readPath: `HistoricalReporting/client-profiles/${clientId}`,
        correctionPath: "HistoricalReporting/client-profiles",
        rowScopeFields: ["businessName", "primaryContactName", "streetAddress", "city", "zipCode"],
        rowExcludeFields: ["mileageRate", "adminFee", "adminFeeType", "billingPeriodMode"],
        valueFields: [
          { key: "businessName", label: "Business Name" },
          { key: "primaryContactName", label: "Primary Contact" },
          { key: "streetAddress", label: "Address" },
          { key: "city", label: "City" },
          { key: "zipCode", label: "Zip" },
        ],
      }],
    });
  };

  const openClientFinancialSettingsHistory = () => {
    const clientId = Number(selectedClient?.clientId || 0);
    if (!clientId) return;
    setHistoryView({
      title: "Financial Settings History",
      contextLabel: `${selectedClient?.businessName || `Client ${clientId}`} - Financial Settings`,
      configs: [{
        sectionLabel: "Financial Settings",
        readPath: `HistoricalReporting/client-financial-settings/${clientId}`,
        correctionPath: "HistoricalReporting/client-financial-settings",
        rowScopeFields: ["mileageRate", "adminFee", "adminFeeType", "billingPeriodMode"],
        rowExcludeFields: ["businessName", "primaryContactName", "streetAddress", "city", "zipCode"],
        valueFields: [
          { key: "mileageRate", label: "Mileage Rate", format: formatCurrency },
          { key: "adminFee", label: "Admin Fee", format: formatPercentOrCurrency },
          { key: "adminFeeType", label: "Admin Fee Type" },
          { key: "billingPeriodMode", label: "Billing Period Mode", format: formatBillingPeriodMode },
        ],
      }],
    });
  };

  const openClientContactHistory = (contact: ContactRecord) => {
    const contactId = Number(contact.id ?? contact.contactId ?? 0);
    if (!contactId) return;

    const name = `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || `Contact ${contactId}`;
    setHistoryView({
      title: "Client Contact History",
      contextLabel: `${name} - Client Contact`,
      configs: [{
        sectionLabel: "Client Contact",
        readPath: `HistoricalReporting/client-contacts/${contactId}`,
        correctionPath: "HistoricalReporting/client-contacts",
        rowScopeFields: ["firstName", "lastName", "emailAddress", "phoneNumber"],
        valueFields: [
          { key: "firstName", label: "First Name" },
          { key: "lastName", label: "Last Name" },
          { key: "emailAddress", label: "Email" },
          { key: "phoneNumber", label: "Phone" },
        ],
      }],
    });
  };

  const loadClients= useCallback(async () => {
    if (!api) return null;
    setClientsLoading(true);
    try {
      const data = await api.get("clientinfo");
      const list = Array.isArray(data)
        ? data.map((item) => normalizeClient((item ?? {}) as Record<string, unknown>))
        : [];

      list.sort((a, b) =>
        (a.businessName || "")
          .toLowerCase()
          .localeCompare((b.businessName || "").toLowerCase()),
      );

      setClients(list);
      return list;
    } catch (err) {
      console.error("❌ Error loading clients:", err);
      showStatus("error", isPM ? "Failed to load assigned clients." : "Failed to load clients.");
      return null;
    } finally {
      setClientsLoading(false);
    }
  }, [api, isPM, showStatus]);

  const loadClientContacts = useCallback(async (clientId: any) => {
    if (!api) return;
    try {
      const data = await api.get(`clientcontacts/${clientId}`);
      setClientContacts(Array.isArray(data) ? data : []);
    } catch {
      setClientContacts([]);
    }
  }, [api]);

  const loadStates = useCallback(async () => {
    if (!api) return;
    try {
      const data = await api.get("stateinfo/");
      setStates(Array.isArray(data) ? data : []);
    } catch {
      setStates([]);
    }
  }, [api]);

  const loadUsage = useCallback(async () => {
    if (!api || !canEdit) {
      setUsage(null);
      setUsageLoading(false);
      return null;
    }

    setUsageLoading(true);
    try {
      const data = await api.get("subscription/usage");
      const normalized = data && typeof data === "object"
        ? normalizeUsage(data as Record<string, unknown>)
        : null;
      setUsage(normalized);
      return normalized;
    } catch (err) {
      console.error("❌ Error loading subscription usage:", err);
      showStatus("error", "Failed to load subscription usage.");
      setUsage(null);
      return null;
    } finally {
      setUsageLoading(false);
    }
  }, [api, canEdit, showStatus]);

  useEffect(() => {
    if (!api) return;
    loadClients();
    loadStates();
    loadUsage();
    api.get("userinfo/lookup/users?includeCompanyExpense=false").then((data: unknown) => {
      setUsers(Array.isArray(data) ? data : []);
    }).catch(() => setUsers([]));
  }, [api, loadClients, loadStates, loadUsage]);

  useEffect(() => {
    if (selectedClient) {
      if (clientCreationBlocked && Number(selectedClient.clientId ?? 0) === 0) {
        if (clients.length) {
          const first = { ...clients[0] };
          setSelectedClient(first);
          loadClientContacts(first.clientId);
        } else {
          setSelectedClient(null);
          setClientContacts([]);
        }
      }
      return;
    }

    if (!clients.length) {
      if (!clientsLoading && isPM) {
        setSelectedClient(null);
        setClientContacts([]);
      }
      if (!clientsLoading && canEdit && !clientCreationBlocked) {
        setSelectedClient(createEmptyClient());
        setClientContacts([]);
      }
      return;
    }

    const first = { ...clients[0] };
    setSelectedClient(first);
    loadClientContacts(first.clientId);
  }, [clients, selectedClient, loadClientContacts, clientsLoading, canEdit, isPM, clientCreationBlocked]);

  useEffect(() => {
    if (error) {
      showStatus("error", typeof error === "string" ? error : (error as any).message || "Authentication error.");
    }
  }, [error, showStatus]);

  const switchClients = (clientId: any) => {
    if (clientId === "0") {
      if (clientCreationBlocked) {
        showStatus("error", clientLimitMessage || "Client limit information is still loading.");
        return;
      }
      setSelectedClient(createEmptyClient());
      setClientContacts([]);
      return;
    }

    const client = clients.find((c) => String(c.clientId) === String(clientId));
    setSelectedClient(client ? { ...client } : null);
    loadClientContacts(clientId);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setSelectedClient((prev: any) => ({
      ...(prev || createEmptyClient()),
      [name]: value,
    }));
  };

  const handleContactInputChange = (e: any) => {
    const { name, value } = e.target;
    setNewContact((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdminFeeTypeChange = (nextValue: any) => {
    setSelectedClient((prev: any) => ({
      ...(prev || createEmptyClient()),
      adminFeeType: nextValue,
    }));
  };

  const getClientSchedulingChangedFields = (client: ClientInfo) => {
    if (Number(client.clientId ?? 0) === 0) return [];
    const original = clients.find((c) => Number(c.clientId) === Number(client.clientId));
    if (!original) return [];

    const fields: Array<[keyof ClientInfo, string, "string" | "number"]> = [
      ["businessName", "Business Name", "string"],
      ["primaryContactId", "Primary Contact", "number"],
      ["streetAddress", "Street Address", "string"],
      ["city", "City", "string"],
      ["state", "State", "number"],
      ["zipCode", "Zip Code", "string"],
      ["mileageRate", "Mileage Rate", "number"],
      ["adminFee", "Admin Fee", "number"],
      ["adminFeeType", "Admin Fee Type", "string"],
      ["billingPeriodMode", "Billing Period Mode", "string"],
    ];

    return fields
      .filter(([key, , kind]) => {
        if (key === "billingPeriodMode") {
          return normalizeBillingPeriodMode((original as any)[key]) !== normalizeBillingPeriodMode((client as any)[key]);
        }
        if (kind === "number") {
          return Number((original as any)[key] || 0) !== Number((client as any)[key] || 0);
        }
        return String((original as any)[key] || "").trim() !== String((client as any)[key] || "").trim();
      })
      .map(([, label]) => label);
  };

  const saveClient = async (effectiveStartDate?: string | null) => {
    if (isPM) return;
    if (!api || !selectedClient) return;
    if (tierLoading) {
      showStatus("error", EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE);
      return;
    }

    if (!String(selectedClient.businessName || "").trim()) {
      showStatus("error", "Business Name is required.");
      return;
    }

    const isCreate = Number(selectedClient.clientId ?? 0) === 0;
    const selectedBillingPeriodMode = selectedClient.billingPeriodMode || "unset";

    // Build clean payload — only API-expected fields
    const payload = withEffectiveStartDateIfAllowed({
      businessName: String(selectedClient.businessName || "").trim(),
      primaryContactId: selectedClient.primaryContactId ?? null,
      streetAddress: selectedClient.streetAddress,
      city: selectedClient.city,
      state: selectedClient.state,
      zipCode: selectedClient.zipCode,
      mileageRate: selectedClient.mileageRate,
      adminFee: selectedClient.adminFee,
      adminFeeType: selectedClient.adminFeeType,
      billingPeriodMode: selectedBillingPeriodMode,
      ...(!isCreate ? { clientId: selectedClient.clientId } : {}),
    }, isCreate ? todayYmd() : effectiveStartDate, canUseEffectiveHistory);

    try {
      const saved = isCreate
        ? await api.post<ClientInfo>("clientinfo", payload)
        : await api.put<ClientInfo>(`clientinfo/${selectedClient.clientId}`, payload);

      const savedClient = normalizeClient((saved ?? {}) as Record<string, unknown>);
      const newId = Number(savedClient.clientId || selectedClient.clientId || 0);

      const freshList = await loadClients();
      await loadUsage();
      if (freshList) {
        const fresh = freshList.find((c) => Number(c.clientId) === newId);
        if (fresh) {
          setSelectedClient({ ...fresh });
          await loadClientContacts(fresh.clientId);
        } else if (newId > 0) {
          setSelectedClient(savedClient);
          await loadClientContacts(savedClient.clientId);
        }
      }

      showStatus(
        "success",
        isCreate ? "Client created successfully." : "Client updated successfully.",
      );
    } catch (err) {
      console.error("❌ Error updating client:", err);
      throw err;
    }
  };

  const performClientSave = async (effectiveStartDate?: string | null, fromModal = false) => {
    try {
      if (fromModal) {
        setEffectiveDateModal((prev) => ({ ...prev, saving: true, error: "" }));
      }
      await saveClient(effectiveStartDate);
      if (fromModal) {
        setEffectiveDateModal({ show: false, changedFields: [], saving: false, error: "" });
      }
    } catch (err) {
      if (fromModal) {
        setEffectiveDateModal((prev) => ({
          ...prev,
          saving: false,
          error: getEffectiveHistoryErrorMessage(err, getErrorMessage(err, "Failed to save client.")),
        }));
      } else {
        showStatus("error", getEffectiveHistoryErrorMessage(err, getErrorMessage(err, "Failed to save client.")));
      }
    } finally {
      if (fromModal) {
        setEffectiveDateModal((prev) => (
          prev.show ? { ...prev, saving: false } : prev
        ));
      }
    }
  };

  const updateClient = async (e: any) => {
    e.preventDefault();
    if (!selectedClient) return;

    const changedFields = getClientSchedulingChangedFields(selectedClient);
    if (changedFields.length > 0 && canUseEffectiveHistory) {
      setEffectiveDateModal({
        show: true,
        changedFields,
        saving: false,
        error: "",
      });
      return;
    }

    await performClientSave(null);
  };

  const deleteClient = async () => {
    if (isPM) return;
    if (!api || !selectedClient || Number(selectedClient.clientId) === 0) return;

    try {
      await api.delete(`clientinfo/${selectedClient.clientId}`);

      setClients((prev) => {
        const next = prev.filter(
          (c) => Number(c.clientId) !== Number(selectedClient.clientId),
        );
        const newSel = next[0] || null;
        setSelectedClient(newSel);
        if (newSel) {
          loadClientContacts(newSel.clientId);
        } else {
          setClientContacts([]);
        }
        return next;
      });

      showStatus("success", "Client deleted successfully.");
    } catch (err) {
      console.error("❌ Error deleting client:", err);
      showStatus("error", "Failed to delete client.");
    }
  };

  const addClientContact = async () => {
    const clientId = Number(selectedClient?.clientId);
    if (!api || !clientId) return;
    if (tierLoading) {
      showStatus("error", EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE);
      return;
    }

    if (
      !newContact.firstName.trim() ||
      !newContact.lastName.trim()
    ) {
      showStatus("error", "Enter first name and last name.");
      return;
    }

    try {
      await api.post(`clientcontacts/${clientId}`, withEffectiveStartDateIfAllowed({ ...newContact, clientId }, todayYmd(), canUseEffectiveHistory));
      setNewContact({ firstName: "", lastName: "", emailAddress: "", phoneNumber: "" });
      await loadClientContacts(clientId);
      showStatus("success", "Client contact added successfully.");
    } catch (err) {
      console.error("❌ Error adding contact:", err);
      showStatus("error", "Failed to add client contact.");
    }
  };

  const deleteClientContact = async (contactId: any) => {
    if (!api) return;

    try {
      await api.delete(`clientcontacts/contact/${contactId}`);
      setClientContacts((prev) =>
        prev.filter((c) => (c.id ?? c.contactId) !== contactId),
      );
      showStatus("success", "Client contact removed successfully.");
    } catch (err: any) {
      console.error("❌ Error deleting contact:", err);
      const msg = err?.details?.message || err?.message || "Failed to remove client contact.";
      showStatus("error", msg);
    }
  };

  const setPrimaryContact = async (contactId: number) => {
    if (!api || !selectedClient) return;
    const clientId = Number(selectedClient.clientId);
    if (!clientId) return;

    try {
      await api.put(`clientinfo/${clientId}/primary-contact`, { primaryContactId: contactId });
      setSelectedClient((prev: any) => ({ ...prev, primaryContactId: contactId }));
      showStatus("success", "Primary contact updated.");
    } catch (err) {
      console.error("❌ Error setting primary contact:", err);
      showStatus("error", getErrorMessage(err, "Failed to set primary contact."));
    }
  };

  const startEditContact = (contact: ContactRecord) => {
    const cid = contact.id ?? contact.contactId;
    setEditingContactId(cid ?? null);
    setEditingContact({
      firstName: contact.firstName || "",
      lastName: contact.lastName || "",
      emailAddress: contact.emailAddress || "",
      phoneNumber: (contact as any).phoneNumber || "",
    });
  };

  const cancelEditContact = () => {
    setEditingContactId(null);
  };

  const getContactSchedulingChangedFields = (contactId: number, nextContact: typeof editingContact) => {
    const original = clientContacts.find((contact) => Number(contact.id ?? contact.contactId) === Number(contactId));
    if (!original) return [];

    const fields: Array<[keyof typeof editingContact, string]> = [
      ["firstName", "First Name"],
      ["lastName", "Last Name"],
      ["emailAddress", "Email"],
      ["phoneNumber", "Phone"],
    ];

    return fields
      .filter(([key]) => String((original as any)[key] || "").trim() !== String(nextContact[key] || "").trim())
      .map(([, label]) => label);
  };

  const performContactSave = async (effectiveStartDate?: string | null, fromModal = false) => {
    if (!api || !editingContactId || !selectedClient) return;
    if (tierLoading) {
      showStatus("error", EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE);
      return;
    }
    if (!editingContact.firstName.trim() || !editingContact.lastName.trim()) {
      showStatus("error", "First name and last name are required.");
      return;
    }
    try {
      if (fromModal) {
        setContactEffectiveDateModal((prev) => ({ ...prev, saving: true, error: "" }));
      }

      await api.put(`clientcontacts/contact/${editingContactId}`, withEffectiveStartDateIfAllowed({
        ...editingContact,
        clientId: selectedClient.clientId,
      }, effectiveStartDate, canUseEffectiveHistory));
      setEditingContactId(null);
      await loadClientContacts(selectedClient.clientId);
      if (fromModal) {
        setContactEffectiveDateModal({ show: false, contactId: 0, changedFields: [], saving: false, error: "" });
      }
      showStatus("success", "Contact updated.");
    } catch (err) {
      console.error("❌ Error updating contact:", err);
      if (fromModal) {
        setContactEffectiveDateModal((prev) => ({
          ...prev,
          saving: false,
          error: getEffectiveHistoryErrorMessage(err, getErrorMessage(err, "Failed to update contact.")),
        }));
      } else {
        showStatus("error", getEffectiveHistoryErrorMessage(err, getErrorMessage(err, "Failed to update contact.")));
      }
    } finally {
      if (fromModal) {
        setContactEffectiveDateModal((prev) => (
          prev.show ? { ...prev, saving: false } : prev
        ));
      }
    }
  };

  const saveEditContact = async () => {
    if (!editingContactId) return;
    if (!editingContact.firstName.trim() || !editingContact.lastName.trim()) {
      showStatus("error", "First name and last name are required.");
      return;
    }

    const changedFields = getContactSchedulingChangedFields(editingContactId, editingContact);
    if (changedFields.length === 0) {
      setEditingContactId(null);
      return;
    }

    if (!canUseEffectiveHistory) {
      performContactSave(null);
      return;
    }

    setContactEffectiveDateModal({
      show: true,
      contactId: editingContactId,
      changedFields,
      saving: false,
      error: "",
    });
  };

  const canAddContact =
    Number(selectedClient?.clientId) > 0 &&
    !!newContact.firstName.trim() &&
    !!newContact.lastName.trim();

  const hasBillingControls = hasFeature("billing_controls");
  const selectedClientId = Number(selectedClient?.clientId ?? 0);

  return (
    <>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authRequest}
      >
        <>

          <div className="main-articles">
            <div className={styles['clients-shell']}>
              <form onSubmit={updateClient} noValidate>
                <div className={styles['clients-card']} data-testid="client-details-card">
                  <div className={styles['clients-card-header']}>
                    <h2 className={styles['clients-card-header-title']}>Client Details</h2>
                    {canUseEffectiveHistory && Number(selectedClient?.clientId || 0) > 0 && (
                      <HistoryIconButton
                        onClick={openClientProfileHistory}
                        label={`View effective history for ${selectedClient?.businessName || "client"} details`}
                        testId="client-details-history"
                      />
                    )}
                  </div>
                  <div className={styles['clients-card-body']}>
                  <div className={styles['clients-form-grid']}>
                    <div className={`${styles['clients-field']} ${styles['clients-field--half']}`}>
                      <label className={styles['clients-label']} htmlFor="clientSelect">
                        Select Client
                      </label>
                      <SearchableSelect
                        id="clientSelect"
                        className={styles['clients-control']}
                        options={[
                          ...(canEdit && !clientCreationBlocked ? [{ value: 0, label: "New Client" }] : []),
                          ...clients.map((c) => ({ value: c.clientId, label: c.businessName })),
                        ]}
                        value={selectedClientId}
                        onChange={(val) => switchClients(String(val ?? "0"))}
                        placeholder="Search clients..."
                        disabled={clientsLoading}
                      />
                      {clientLimitReached && (
                        <p className={styles['clients-limit-message']} data-testid="client-limit-message">
                          {clientLimitMessage}
                        </p>
                      )}
                    </div>

                    {isPM && !clientsLoading && !selectedClient ? (
                      <div className={`${styles['clients-field']} ${styles['clients-field--full']}`}>
                        <div className={styles['clients-empty']} data-testid="pm-no-assigned-clients">
                          <p className={styles['clients-empty__title']}>No assigned clients</p>
                          <p className={styles['clients-empty__subtitle']}>
                            You are not assigned to any clients yet. Contact an administrator if you need access.
                          </p>
                        </div>
                      </div>
                    ) : (
                    <>
                    <div className={`${styles['clients-field']} ${styles['clients-field--half']}`}>
                      <label className={styles['clients-label']} htmlFor="businessName">
                        Business Name
                      </label>
                      <input
                        id="businessName"
                        className={styles['clients-control']}
                        type="text"
                        name="businessName"
                        value={selectedClient?.businessName || ""}
                        onChange={handleInputChange}
                        readOnly={isPM}
                        required
                        aria-required="true"
                        aria-invalid={!String(selectedClient?.businessName || "").trim()}
                      />
                    </div>

                    <div className={`${styles['clients-field']} ${styles['clients-field--full']}`}>
                      <label className={styles['clients-label']} htmlFor="streetAddress">
                        Address
                      </label>
                      <input
                        id="streetAddress"
                        className={styles['clients-control']}
                        type="text"
                        name="streetAddress"
                        value={selectedClient?.streetAddress || ""}
                        onChange={handleInputChange}
                        readOnly={isPM}
                      />
                    </div>

                    <div className={`${styles['clients-field']} ${styles['clients-field--third']}`}>
                      <label className={styles['clients-label']} htmlFor="city">
                        City
                      </label>
                      <input
                        id="city"
                        className={styles['clients-control']}
                        type="text"
                        name="city"
                        value={selectedClient?.city || ""}
                        onChange={handleInputChange}
                        readOnly={isPM}
                      />
                    </div>

                    <div className={`${styles['clients-field']} ${styles['clients-field--third']}`}>
                      <label className={styles['clients-label']} htmlFor="state">
                        State
                      </label>
                        <SearchableSelect
                          id="state"
                          className={styles['clients-control']}
                          options={states.map((s) => ({ value: s.stateId, label: s.name }))}
                          value={selectedClient?.state ? Number(selectedClient.state) : null}
                          onChange={(val) => setSelectedClient((prev: any) => ({ ...(prev || createEmptyClient()), state: val }))}
                          placeholder="Search states..."
                          disabled={isPM || !states.length}
                        />
                    </div>

                    <div className={`${styles['clients-field']} ${styles['clients-field--third']}`}>
                      <label className={styles['clients-label']} htmlFor="zipCode">
                        Zip
                      </label>
                      <input
                        id="zipCode"
                        className={styles['clients-control']}
                        type="text"
                        name="zipCode"
                        value={selectedClient?.zipCode || ""}
                        onChange={handleInputChange}
                        readOnly={isPM}
                      />
                    </div>

                    {hasBillingControls && (
                    <>
                    <div className={`${styles['clients-section-heading']} ${styles['clients-field--full']}`}>
                      <span>Financial Settings</span>
                      {canUseEffectiveHistory && Number(selectedClient?.clientId || 0) > 0 && (
                        <HistoryIconButton
                          onClick={openClientFinancialSettingsHistory}
                          label={`View effective history for ${selectedClient?.businessName || "client"} financial settings`}
                          testId="client-financial-settings-history"
                        />
                      )}
                    </div>

                    <div className={`${styles['clients-field']} ${styles['clients-field--half']}`}>
                      <label className={styles['clients-label']} htmlFor="mileageRate">
                        Mileage Rate
                      </label>
                      <input
                        id="mileageRate"
                        className={styles['clients-control']}
                        type="text"
                        name="mileageRate"
                        value={selectedClient?.mileageRate ?? 0}
                        onChange={handleInputChange}
                        readOnly={isPM}
                      />
                    </div>

                    <div className={`${styles['clients-field']} ${styles['clients-field--half']}`}>
                      <label className={styles['clients-label']} htmlFor="adminFee">
                        Admin Fee
                      </label>
                      <div className={styles['clients-adminfee-row']}>
                        <input
                          id="adminFee"
                          className={styles['clients-control']}
                          type="tel"
                          name="adminFee"
                          value={selectedClient?.adminFee ?? 0}
                          onChange={handleInputChange}
                          readOnly={isPM}
                        />

                        <div
                          className={styles['clients-fee-toggle']}
                          role="tablist"
                          aria-label="Admin fee type"
                        >
                          <button
                            type="button"
                            className={`${styles['clients-fee-toggle__item']}${
                              (selectedClient?.adminFeeType || "percent") === "percent"
                                ? ` ${styles['clients-fee-toggle__item--active']}`
                                : ""
                            }`}
                            onClick={() => handleAdminFeeTypeChange("percent")}
                            aria-pressed={
                              (selectedClient?.adminFeeType || "percent") ===
                              "percent"
                            }
                            disabled={isPM}
                          >
                            Percent
                          </button>

                          <button
                            type="button"
                            className={`${styles['clients-fee-toggle__item']}${
                              selectedClient?.adminFeeType === "flat"
                                ? ` ${styles['clients-fee-toggle__item--active']}`
                                : ""
                            }`}
                            onClick={() => handleAdminFeeTypeChange("flat")}
                            aria-pressed={selectedClient?.adminFeeType === "flat"}
                            disabled={isPM}
                          >
                            Flat Rate
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className={`${styles['clients-field']} ${styles['clients-field--half']}`}>
                      <label className={styles['clients-label']} htmlFor="clientBillingPeriodMode">
                        Billing Period Mode
                      </label>
                      <select
                        id="clientBillingPeriodMode"
                        className={styles['clients-control']}
                        name="billingPeriodMode"
                        value={selectedClient?.billingPeriodMode || "unset"}
                        onChange={handleInputChange}
                        disabled={isPM}
                      >
                        {BILLING_PERIOD_MODE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    </>
                    )}

                    {canEdit && (
                    <div className={styles['clients-form-actions']}>
                      {Number(selectedClient?.clientId) > 0 && (
                        <button
                          type="button"
                          className={styles['clients-btn-danger']}
                          onClick={() => setShowDeleteModal(true)}
                        >
                          Delete
                        </button>
                      )}
                        <button
                          type="submit"
                          className={styles['clients-btn']}
                          disabled={
                            tierLoading ||
                            (selectedClientId === 0 &&
                              clientCreationBlocked)
                          }
                        >
                          {selectedClientId === 0
                            ? clientLimitReached
                              ? `${maxClients} client limit reached`
                              : "Create Client"
                          : "Update"}
                      </button>
                    </div>
                    )}
                    </>
                    )}
                  </div>
                  </div>
                </div>
              </form>

              <div className={styles['clients-card']} data-testid="client-contacts-card">
                <div className={styles['clients-card-header']}>
                  <h2 className={styles['clients-card-header-title']}>Client Contacts</h2>
                </div>
                {Number(selectedClient?.clientId) > 0 ? (
                  <>
                    {canEdit && (
                    <div className={styles['clients-card-body']}>
                      <div className={styles['clients-contact-composer']}>
                        <div className={styles['clients-field']}>
                          <label
                            className={styles['clients-label']}
                            htmlFor="contactFirstName"
                          >
                            First Name
                          </label>
                          <input
                            id="contactFirstName"
                            className={styles['clients-control']}
                            type="text"
                            name="firstName"
                            value={newContact.firstName}
                            onChange={handleContactInputChange}
                            placeholder="First Name"
                          />
                        </div>

                        <div className={styles['clients-field']}>
                          <label className={styles['clients-label']} htmlFor="contactLastName">
                            Last Name
                          </label>
                          <input
                            id="contactLastName"
                            className={styles['clients-control']}
                            type="text"
                            name="lastName"
                            value={newContact.lastName}
                            onChange={handleContactInputChange}
                            placeholder="Last Name"
                          />
                        </div>

                        <div className={styles['clients-field']}>
                          <label className={styles['clients-label']} htmlFor="contactEmail">
                            Email Address
                          </label>
                          <input
                            id="contactEmail"
                            className={styles['clients-control']}
                            type="email"
                            name="emailAddress"
                            value={newContact.emailAddress}
                            onChange={handleContactInputChange}
                            placeholder="Email Address"
                          />
                        </div>

                        <div className={styles['clients-field']}>
                          <label className={styles['clients-label']} htmlFor="contactPhone">
                            Phone Number
                          </label>
                          <input
                            id="contactPhone"
                            className={styles['clients-control']}
                            type="tel"
                            name="phoneNumber"
                            value={newContact.phoneNumber}
                            onChange={handleContactInputChange}
                            placeholder="Phone Number"
                          />
                        </div>

                        <div className={styles['clients-contact-actions']}>
                          <button
                            type="button"
                            className={`${styles['clients-btn']} ${styles['clients-contact-add-btn']}`}
                            onClick={addClientContact}
                            disabled={tierLoading || !canAddContact}
                          >
                            <PlusIcon />
                            Add Contact
                          </button>
                        </div>
                      </div>
                    </div>
                    )}

                    <div className={styles['clients-card-subheader']}>
                      <span>Saved Contacts</span>
                    </div>

                    {clientContacts.length === 0 ? (
                      <div className={styles['clients-empty']}>
                        <div className={styles['clients-empty__icon']}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="8" r="4" stroke="#d1d5db" strokeWidth="1.5" />
                            <path d="M4 20c0-3.3 2.7-6 6-6h4c3.3 0 6 2.7 6 6" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </div>
                        <p className={styles['clients-empty__title']}>No contacts</p>
                        <p className={styles['clients-empty__subtitle']}>No client contacts have been added yet.</p>
                      </div>
                    ) : (
                      <>
                        <div className={styles['clients-contact-list-header']}>
                          <div>Name</div>
                          <div>Email</div>
                          <div>Phone</div>
                          <div></div>
                        </div>

                        {clientContacts.map((contact, i) => {
                          const key =
                            contact.id ??
                            contact.contactId ??
                            `${contact.emailAddress}-${contact.firstName}-${contact.lastName}-${i}`;

                          const contactId = contact.id ?? contact.contactId;
                          const isPrimary = contactId === selectedClient?.primaryContactId;

                          return (
                            <div className={styles['clients-contact-row']} key={key} data-testid={`client-contact-${contact.id ?? contact.contactId ?? i}`}>
                              {editingContactId === contactId ? (
                                <>
                                  <div className={styles['clients-contact-row__cell']}>
                                    <input
                                      className={styles['clients-control']}
                                      value={editingContact.firstName}
                                      onChange={(e) => setEditingContact((p) => ({ ...p, firstName: e.target.value }))}
                                      placeholder="First Name"
                                      style={{ marginBottom: 4 }}
                                    />
                                    <input
                                      className={styles['clients-control']}
                                      value={editingContact.lastName}
                                      onChange={(e) => setEditingContact((p) => ({ ...p, lastName: e.target.value }))}
                                      placeholder="Last Name"
                                    />
                                  </div>
                                  <div className={styles['clients-contact-row__cell']}>
                                    <input
                                      className={styles['clients-control']}
                                      type="email"
                                      value={editingContact.emailAddress}
                                      onChange={(e) => setEditingContact((p) => ({ ...p, emailAddress: e.target.value }))}
                                      placeholder="Email"
                                    />
                                  </div>
                                  <div className={styles['clients-contact-row__cell']}>
                                    <input
                                      className={styles['clients-control']}
                                      type="tel"
                                      value={editingContact.phoneNumber}
                                      onChange={(e) => setEditingContact((p) => ({ ...p, phoneNumber: e.target.value }))}
                                      placeholder="Phone"
                                    />
                                  </div>
                                  <div className={styles['clients-contact-row__actions']}>
                                    <button type="button" className={styles['clients-icon-btn']} onClick={saveEditContact} disabled={tierLoading} aria-label="Save contact" title="Save">
                                      <SaveIcon />
                                    </button>
                                    <button type="button" className={styles['clients-icon-btn']} onClick={cancelEditContact} aria-label="Cancel edit" title="Cancel">
                                      <CancelIcon />
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <>
                              <div className={styles['clients-contact-row__cell']}>
                                {contact.firstName} {contact.lastName}
                                {isPrimary && (
                                  <span className={styles['clients-contact-badge']}>Primary</span>
                                )}
                              </div>

                              <div className={styles['clients-contact-row__cell']}>
                                <a
                                  className={styles['clients-contact-row__email']}
                                  href={`mailto:${contact.emailAddress}`}
                                >
                                  {contact.emailAddress || "—"}
                                </a>
                              </div>

                              <div className={styles['clients-contact-row__cell']}>
                                {contact.phoneNumber || "—"}
                              </div>

                              <div className={styles['clients-contact-row__actions']}>
                                {canEdit && (
                                  <>
                                    {!isPrimary && (
                                      <button
                                        type="button"
                                        className={styles['clients-icon-btn']}
                                        onClick={() => setPrimaryContact(contactId!)}
                                        aria-label={`Set ${contact.firstName} ${contact.lastName} as primary`}
                                        title="Set as primary contact"
                                      >
                                        <StarIcon />
                                      </button>
                                    )}
                                    {canUseEffectiveHistory && (
                                    <button
                                      type="button"
                                      className={styles['clients-icon-btn']}
                                      onClick={() => openClientContactHistory(contact)}
                                      aria-label={`View effective history for ${contact.firstName} ${contact.lastName}`}
                                      title="View effective history"
                                    >
                                      <HistoryIcon />
                                    </button>
                                    )}
                                    <button
                                      type="button"
                                      className={styles['clients-icon-btn']}
                                      onClick={() => startEditContact(contact)}
                                      aria-label={`Edit ${contact.firstName} ${contact.lastName}`}
                                      title="Edit contact"
                                    >
                                      <EditIcon />
                                    </button>
                                    {!isPrimary && (
                                    <button
                                      type="button"
                                      className={`${styles['clients-icon-btn']} ${styles['clients-icon-btn--danger']}`}
                                      onClick={() => deleteClientContact(contactId)}
                                      aria-label={`Delete ${contact.firstName} ${contact.lastName}`}
                                      title="Delete contact"
                                    >
                                      <TrashIcon />
                                    </button>
                                    )}
                                  </>
                                )}
                              </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </>
                ) : (
                  <div className={styles['clients-empty']}>
                    <div className={styles['clients-empty__icon']}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M12 8v4m0 4h.01" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" />
                        <circle cx="12" cy="12" r="9" stroke="#d1d5db" strokeWidth="1.5" />
                      </svg>
                    </div>
                    <p className={styles['clients-empty__title']}>Client not saved</p>
                    <p className={styles['clients-empty__subtitle']}>Save the client before adding contacts.</p>
                  </div>
                )}
              </div>

              {selectedClient && api && canEdit && (
                <FeatureGate feature="billing_controls" minLevel="advanced">
                  <ClientBillingRolesCard
                    clientId={Number(selectedClient.clientId) || 0}
                    api={api}
                    showStatus={showStatus}
                    canUseEffectiveHistory={canUseEffectiveHistory}
                  />
                </FeatureGate>
              )}

              {canEdit && (
              <FeatureGate feature="approval_workflow" minLevel="advanced">
                {selectedClient && api && (
                  <div className={styles['clients-card']} data-testid="approval-workflow-card">
                    <div className={styles['clients-card-header']}>
                      <h2 className={styles['clients-card-header-title']}>Approval Workflow</h2>
                    </div>
                    <ApproverList
                      entityType="client"
                      entityId={Number(selectedClient.clientId) || 0}
                      users={users}
                      api={api}
                      hideHeader
                    />
                  </div>
                )}
              </FeatureGate>
              )}
              {canEdit && (
              <FeatureGate feature="billing_controls" minLevel="advanced">
                {selectedClient && api && (
                <div className={styles['clients-card']} data-testid="pm-assignment-card">
                  <div className={styles['clients-card-header']}>
                    <h2 className={styles['clients-card-header-title']}>Prospect Managers</h2>
                  </div>
                  <ProjectManagerList
                    clientId={Number(selectedClient.clientId) || 0}
                    users={users}
                    api={api}
                    hideHeader
                  />
                </div>
                )}
              </FeatureGate>
              )}
            </div>
          </div>
        </>
      </MsalAuthenticationTemplate>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        role="dialog"
        aria-modal={true}
        aria-labelledby="deleteClientTitle"
        data-testid="delete-client-modal"
      >
        <Modal.Header closeButton className="bg-light text-dark">
          <Modal.Title id="deleteClientTitle">Delete Client</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light text-dark">
          Are you sure you want to delete this client and all associated data?
        </Modal.Body>
        <Modal.Footer className="bg-light text-dark">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            No
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              deleteClient();
              setShowDeleteModal(false);
            }}
          >
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {status && (
        <Toast
          message={statusMessage}
          type={status as "success" | "error"}
          onClose={() => { setStatus(null); setStatusMessage(""); }}
        />
      )}
      <SaveEffectiveDateModal
        show={effectiveDateModal.show}
        changedFieldLabels={effectiveDateModal.changedFields}
        saving={effectiveDateModal.saving}
        errorMessage={effectiveDateModal.error}
        onCancel={() => setEffectiveDateModal({ show: false, changedFields: [], saving: false, error: "" })}
        onConfirm={(effectiveStartDate) => performClientSave(effectiveStartDate, true)}
      />
      <SaveEffectiveDateModal
        show={contactEffectiveDateModal.show}
        changedFieldLabels={contactEffectiveDateModal.changedFields}
        saving={contactEffectiveDateModal.saving}
        errorMessage={contactEffectiveDateModal.error}
        onCancel={() => setContactEffectiveDateModal({ show: false, contactId: 0, changedFields: [], saving: false, error: "" })}
        onConfirm={(effectiveStartDate) => performContactSave(effectiveStartDate, true)}
      />
      <HistoricalReportingHistory
        show={!!historyView}
        api={api}
        enabled={canUseEffectiveHistory}
        title={historyView?.title || "Effective History"}
        contextLabel={historyView?.contextLabel || ""}
        config={null}
        configs={historyView?.configs || []}
        onClose={() => setHistoryView(null)}
        onChanged={() => loadClients()}
        onCorrected={(message) => showStatus("success", message)}
      />
    </>
  );
};

export default Clients;
